import { useIsAdmin } from "@/hooks/useIsAdmin";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

interface AuthGuardProps {
  children: React.ReactNode;
  requireBookOwnership?: boolean;
}

export function AuthGuard({
  children,
  requireBookOwnership = false
}: AuthGuardProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [ownershipChecked, setOwnershipChecked] = useState(false);
  const { data: isAdmin } = useIsAdmin();
  const { bookId } = useParams();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const {
          data: { user }
        } = await supabase.auth.getUser();
        setUser(user);

        if (user && requireBookOwnership && bookId) {
          // Check if user owns the book
          const { data: book } = await supabase
            .from("books")
            .select("user_id")
            .eq("id", bookId)
            .single();

          if (book && book.user_id === user.id) {
            setIsOwner(true);
          }
          setOwnershipChecked(true);
        }
      } catch (error) {
        console.error("Auth check error:", error);
        setOwnershipChecked(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [bookId, requireBookOwnership]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Redirect to signin if not authenticated
  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  // Check book ownership if required
  if (requireBookOwnership && bookId && ownershipChecked) {
    // Allow if user is admin or owner
    if (!isAdmin && !isOwner) {
      return <Navigate to="/my-books" replace />;
    }
  }

  // If requireBookOwnership is true but no bookId, allow (new book creation)
  // If requireBookOwnership is false, just check authentication (already done above)

  return <>{children}</>;
}
